import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { injectedLocalApi } from '@/services/local'
import { extendPaymentMethodsWithParsedIdField } from '@/utils/payment'

type PlainInfo<T> = {
  status: RequestState
  error?: string | null
  data: T
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

interface CheckoutState {
  meta: PlainInfo<Oms.InvoiceMetaData>
  paymentMethods: PlainInfo<Oms.PaymentMethod[]>
  // current selecting payment method
  selectedPaymentMethod: Oms.PaymentMethod | null
  selectedApplePay: boolean
  paymentProgress: PlainInfo<Oms.CheckoutMetaData>
}

const initialState: CheckoutState = {
  meta: {
    status: 'none',
    error: null,
    data: {} as Oms.InvoiceMetaData,
  },
  paymentMethods: {
    status: 'none',
    data: [],
  },
  selectedPaymentMethod: null,
  selectedApplePay: false,
  paymentProgress: {
    status: 'none',
    error: null,
    data: {} as Oms.CheckoutMetaData,
  },
}

export const invoiceSlice = createSlice({
  name: '@invoice',
  initialState,
  reducers: {
    setDefaultInvoiceOrder: (
      state,
      action: PayloadAction<Oms.V4ApiResponse<Oms.InvoiceMetaData>>
    ) => {
      state.meta.data = action.payload.data
      state.meta.error = action?.payload?.errors?.[0]?.errorCode
      state.meta.status = !action?.payload?.errors?.[0]?.errorCode
        ? 'fulfilled'
        : 'rejected'
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      injectedLocalApi.endpoints.getOnlinePaymentOptions.matchFulfilled,
      (state, action) => {
        state.paymentMethods.status = 'fulfilled'
        const filteredPaymentMethods = extendPaymentMethodsWithParsedIdField(
          action.payload.data.payload.paymentOptions.filter(
            (paymentMethod) => paymentMethod.enableOnCheckout
          )
        )
        state.paymentMethods.data = filteredPaymentMethods

        let initializeSelectedPaymentMethod

        if (
          filteredPaymentMethods.length > 0 &&
          action.meta.arg.originalArgs.enabledStripePaymentElement
        ) {
          initializeSelectedPaymentMethod = filteredPaymentMethods.find(
            (paymentMethod) => [7, 20].includes(paymentMethod.type)
          )
        } else if (filteredPaymentMethods.length > 0) {
          initializeSelectedPaymentMethod = filteredPaymentMethods[0]
        }

        if (initializeSelectedPaymentMethod) {
          state.selectedPaymentMethod = initializeSelectedPaymentMethod
        }
      }
    )
  },
})

export const { setDefaultInvoiceOrder } = invoiceSlice.actions
